import * as api from '../api';
import { AnyAction, createStore as reduxCreateStore, Store } from 'redux';

import { resetEnhancer, rootReducer } from './root';

export type Coordinates = api.Places.Geometry;
export type Form = api.PrevisitForm.PrevisitForm;
export type FormFieldWithConfig =
  api.PrevisitFormField.PrevisitFormFieldWithConfig;

export type InsuranceCard =
  api.PatientInsuranceInformation.InsuranceInformationWithDetails;
export type Patient = api.Patient.Patient;
export type PatientFeedback = api.PatientFeedback.PatientFeedback;
export type Language = api.Language.Language;
export type SelectedEhrTimeSlot = api.EhrTimeSlot.EhrTimeSlot &
  Pick<
    FormFieldWithConfig,
    'previsitFormTemplateFieldConfigId' | 'scheduleAppointmentDisplayBy'
  >;

export interface Forms {
  [formId: string]: Form;
}

export interface FormSet {
  id: string;
  completed: Date | null;
  forms: Forms;
  practicePreference: api.Previsit.PracticePreference;
  documents: api.PrevisitDocument.DocumentWithTemplate[];
  encounter?: api.Encounter.Encounter | null;
  practice?: api.Practice.Practice | null;
  provider?: api.User.User | null;
  covidInstruction?: string;
  virtualVisit?: boolean;
  status?: api.PrevisitFormSet.Status | null;
}

export interface FormSets {
  [formSetId: string]: FormSet;
}

export interface Inbound {
  bookedSlot: api.EhrTimeSlot.TimeSlotConfirmation | null;
  selectedProviderId: string | null;
  ehrProviderInformation: api.User.EhrProviderInformationPractice[] | null;
  selectedEhrTimeSlot: SelectedEhrTimeSlot | null;
  provisionalPatientId: string | null;
  urlIdentifier: string | null;
  organizationId: string;
  organizationSlug: string | null;
  isProvisionalPatientReconciled: boolean;
}

export interface All {
  loggedInPatient: Patient | null;
  formSets: FormSets;
  masterPracticePreference: api.Previsit.PracticePreference;
  selectedFormId: string | null;
  selectedFormSetId: string | null;
  patientFeedback: PatientFeedback | null;
  isHeaderHidden: boolean;
  authenticationCode: string | null;
  insuranceCards: {
    data: InsuranceCard[];
    loading: boolean;
    error: string | null;
    updated: Date | null;
  };
  theme: api.PracticeTheme.ThemeResponse | null;
  documentTitle: string | null;
  preferredLanguage: Language;
  multiFormSet: boolean;
  inbound: Inbound;
  coordinates?: Coordinates;
  onCaptchaSuccess: (() => void) | null;
  embedded: boolean | null;
}

// This store typing only applies for children connected under withFetchPatient
export interface Loaded extends All {
  loggedInPatient: Patient;
  masterPracticePreference: api.Previsit.PracticePreference;
  selectedFormId: string;
  patientFeedback: PatientFeedback;
  authenticationCode: string;
}

export interface FormSetMaybeSelected extends Loaded {
  selectedFormSetId: string | null;
}

// This store typing only applies for children that have a specific form set selected
export interface FormSetSelected extends Loaded {
  selectedFormSetId: string;
}

let store: Store<All, AnyAction>;

export function getOrCreateStore(reCreate?: boolean) {
  if (store && !reCreate) return store;
  store = reduxCreateStore(resetEnhancer(rootReducer));
  return store;
}

export function resetStore() {
  localStorage.clear();
  store.dispatch({ type: 'RESET' });
}
